// src/components/Analytics/AnalyticsDashboard.js

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useAnalytics } from '../../hooks/useAnalytics';
import './AnalyticsDashboard.css';
import 'leaflet/dist/leaflet.css';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const AnalyticsDashboard = () => {
  const {
    getStats,
    getPopularArticles,
    getStatsByCountry,
    getStatsByTime,
    getLocationPoints,
  } = useAnalytics();

  const [stats, setStats] = useState(null);
  const [popularArticles, setPopularArticles] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [africaRegions, setAfricaRegions] = useState([]);
  const [timeStats, setTimeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [locationPoints, setLocationPoints] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [statsData, articlesData, countryData, timeData, locationsData] = await Promise.all([
        getStats(period),
        getPopularArticles(period, 10),
        getStatsByCountry(period),
        getStatsByTime(period),
        getLocationPoints(period),
      ]);

      setStats(statsData);
      setPopularArticles(articlesData);
      setCountryStats(countryData);
      setTimeStats(timeData);
      setLocationPoints(locationsData || []);

      // Extraire les régions africaines
      extractAfricaRegions(countryData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractAfricaRegions = (data) => {
    // Régions d'Afrique avec leurs pays
    const africanRegions = {
      'Afrique du Nord': ['Morocco', 'Algeria', 'Tunisia', 'Libya', 'Egypt', 'Sudan'],
      'Afrique de l\'Ouest': ['Senegal', 'Mali', 'Mauritania', 'Niger', 'Nigeria', 'Benin', 'Togo', 'Ghana', 'Ivory Coast', 'Burkina Faso', 'Guinea', 'Sierra Leone', 'Liberia', 'Gambia', 'Guinea-Bissau', 'Cape Verde'],
      'Afrique Centrale': ['Cameroon', 'Central African Republic', 'Chad', 'Congo', 'Democratic Republic of the Congo', 'Equatorial Guinea', 'Gabon', 'Sao Tome and Principe'],
      'Afrique de l\'Est': ['Ethiopia', 'Eritrea', 'Djibouti', 'Somalia', 'Kenya', 'Uganda', 'Rwanda', 'Burundi', 'Tanzania', 'South Sudan'],
      'Afrique Australe': ['South Africa', 'Namibia', 'Botswana', 'Zimbabwe', 'Zambia', 'Malawi', 'Mozambique', 'Angola', 'Lesotho', 'Swaziland', 'Madagascar', 'Mauritius', 'Seychelles', 'Comoros']
    };

    const regionStats = {};

    // Grouper les stats par région
    data.forEach(country => {
      for (const [region, countries] of Object.entries(africanRegions)) {
        if (countries.includes(country.country)) {
          if (!regionStats[region]) {
            regionStats[region] = {
              name: region,
              views: 0,
              visitors: 0,
              countries: []
            };
          }
          regionStats[region].views += country.total_views || 0;
          regionStats[region].visitors += country.unique_visitors || 0;
          regionStats[region].countries.push(country.country);
        }
      }
    });

    // Convertir en tableau et trier par vues
    const regions = Object.values(regionStats)
      .sort((a, b) => b.views - a.views);

    setAfricaRegions(regions);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getDayName = (dayNumber) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[dayNumber] || '';
  };

  if (loading) {
    return (
      <div className="stats-dashboard-wrap">
        <div className="bo-loading">
          <div className="bo-spinner">⏳</div>
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-dashboard-wrap">
      {/* Header avec sélecteur de période */}
      <div className="stats-dash-header">
        <div className="stats-dash-title-group">
          <h1 className="stats-dash-title">📊 Analytics & Statistiques</h1>
          <p className="stats-dash-subtitle">Vue d'ensemble de votre audience et performances</p>
        </div>
        <div className="stats-period-selector">
          <button
            className={`stats-period-btn ${period === 7 ? 'active' : ''}`}
            onClick={() => setPeriod(7)}
          >
            7 jours
          </button>
          <button
            className={`stats-period-btn ${period === 30 ? 'active' : ''}`}
            onClick={() => setPeriod(30)}
          >
            30 jours
          </button>
          <button
            className={`stats-period-btn ${period === 90 ? 'active' : ''}`}
            onClick={() => setPeriod(90)}
          >
            90 jours
          </button>
        </div>
      </div>

      {/* Statistiques principales */}
      <div className="stats-metrics-grid">
        <div className="bo-stat-card">
          <div className="bo-stat-icon">
            👁️
          </div>
          <div className="bo-stat-content">
            <span className="bo-stat-value">{stats?.total_page_views?.toLocaleString() || 0}</span>
            <span className="bo-stat-label">Vues totales</span>
          </div>
        </div>

        <div className="bo-stat-card">
          <div className="bo-stat-icon bo-stat-published">
            👤
          </div>
          <div className="bo-stat-content">
            <span className="bo-stat-value">{stats?.unique_visitors?.toLocaleString() || 0}</span>
            <span className="bo-stat-label">Visiteurs uniques</span>
          </div>
        </div>

        <div className="bo-stat-card">
          <div className="bo-stat-icon bo-stat-draft">
            🔄
          </div>
          <div className="bo-stat-content">
            <span className="bo-stat-value">{stats?.total_sessions?.toLocaleString() || 0}</span>
            <span className="bo-stat-label">Sessions</span>
          </div>
        </div>

        <div className="bo-stat-card">
          <div className="bo-stat-icon bo-stat-scheduled">
            ⏱️
          </div>
          <div className="bo-stat-content">
            <span className="bo-stat-value">0</span>
            <span className="bo-stat-label">Durée moyenne</span>
          </div>
        </div>
      </div>

      {/* Carte interactive et tableaux */}
      <div className="stats-geo-section">
        {/* Carte mondiale centrée sur le Maroc */}
        <div className="stats-card stats-map-card">
          <div className="stats-card-header">
            <h2 className="stats-card-title">🌍 Carte des connexions</h2>
            <p className="stats-card-subtitle">Localisation géographique de vos visiteurs</p>
          </div>
          <div className="stats-map-container" >
            <MapContainer
              center={[28, -10]}
              zoom={3}
              style={{ height: '100%', width: '100%', zindex:'1 !important' }}
              className="stats-leaflet-map"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* Afficher tous les points de connexion avec latitude/longitude */}
              {locationPoints.map((point, index) => {
                // Vérifier que les coordonnées sont valides
                if (point.latitude && point.longitude &&
                    point.latitude >= -90 && point.latitude <= 90 &&
                    point.longitude >= -180 && point.longitude <= 180) {

                  return (
                    <CircleMarker
                      key={index}
                      center={[parseFloat(point.latitude), parseFloat(point.longitude)]}
                      radius={8}
                      pathOptions={{
                        color: '#c22027',
                        fillColor: '#e63946',
                        fillOpacity: 0.6,
                        weight: 2
                      }}
                    >
                      <Popup>
                        <div className="stats-map-popup">
                          <h4>
                            {point.city && point.country ?
                              `${point.city}, ${point.country}` :
                              point.country || 'Localisation inconnue'}
                          </h4>
                          <p><strong>👁️ Vues:</strong> {point.total_views?.toLocaleString()}</p>
                          <p><strong>👤 Visiteurs:</strong> {point.unique_visitors?.toLocaleString()}</p>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                }
                return null;
              })}
            </MapContainer>
          </div>
        </div>

        {/* Tableau par pays */}
        <div className="stats-card">
          <div className="stats-card-header">
            <h2 className="stats-card-title">🌐 Trafic par pays</h2>
            <p className="stats-card-subtitle">Top 10 des pays</p>
          </div>
          <div className="stats-table-container">
            {countryStats && countryStats.length > 0 ? (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Pays</th>
                    <th>Vues</th>
                    <th>Visiteurs</th>
                    <th>Durée moy.</th>
                  </tr>
                </thead>
                <tbody>
                  {countryStats.slice(0, 10).map((country, index) => (
                    <tr key={index}>
                      <td>
                        <span className="stats-country-name">
                          <span className="stats-flag">{country.country_code}</span>
                          {country.country}
                        </span>
                      </td>
                      <td className="stats-number">{country.total_views?.toLocaleString()}</td>
                      <td className="stats-number">{country.unique_visitors?.toLocaleString()}</td>
                      <td className="stats-number">{formatDuration(Math.round(country.avg_session_duration))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="stats-empty">Aucune donnée disponible</div>
            )}
          </div>
        </div>
      </div>

      {/* Régions d'Afrique */}
      <div className="stats-card">
        <div className="stats-card-header">
          <h2 className="stats-card-title">🌍 Trafic par région d'Afrique</h2>
          <p className="stats-card-subtitle">Répartition géographique continentale</p>
        </div>
        <div className="stats-regions-grid">
          {africaRegions.length > 0 ? (
            africaRegions.map((region, index) => (
              <div key={index} className="stats-region-card">
                <h4 className="stats-region-name">{region.name}</h4>
                <div className="stats-region-metrics">
                  <div className="stats-region-metric">
                    <span className="stats-region-icon">👁️</span>
                    <span className="stats-region-value">{region.views?.toLocaleString()}</span>
                  </div>
                  <div className="stats-region-metric">
                    <span className="stats-region-icon">👤</span>
                    <span className="stats-region-value">{region.visitors?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="stats-region-countries">
                  <small>{region.countries.length} pays</small>
                </div>
              </div>
            ))
          ) : (
            <div className="stats-empty">Aucune donnée africaine disponible</div>
          )}
        </div>
      </div>

      {/* Articles populaires */}
      <div className="stats-card">
        <div className="stats-card-header">
          <h2 className="stats-card-title">🔥 Top 10 des articles</h2>
          <p className="stats-card-subtitle">Les contenus les plus consultés</p>
        </div>
        <div className="stats-table-container">
          {popularArticles && popularArticles.length > 0 ? (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Article</th>
                  <th>Vues</th>
                  <th>Visiteurs</th>
                  <th>Durée moy.</th>
                </tr>
              </thead>
              <tbody>
                {popularArticles.map((article, index) => (
                  <tr key={article.id}>
                    <td className="stats-rank">{index + 1}</td>
                    <td className="stats-article-title">
                      <a href={`/news/${article.id}`} target="_blank" rel="noopener noreferrer">
                        {article.title}
                      </a>
                    </td>
                    <td className="stats-number">{article.total_views?.toLocaleString()}</td>
                    <td className="stats-number">{article.unique_visitors?.toLocaleString()}</td>
                    <td className="stats-number">{formatDuration(Math.round(article.avg_duration))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="stats-empty">Aucune donnée disponible</div>
          )}
        </div>
      </div>

      {/* Graphiques temporels */}
      <div className="stats-charts-row">
        {/* Trafic par jour */}
        <div className="stats-card">
          <div className="stats-card-header">
            <h2 className="stats-card-title">📅 Trafic par jour</h2>
            <p className="stats-card-subtitle">Distribution hebdomadaire</p>
          </div>
          <div className="stats-chart-container">
            {timeStats?.by_day_of_week && timeStats.by_day_of_week.length > 0 ? (
              <div className="stats-bar-chart">
                {timeStats.by_day_of_week.map((day) => {
                  const maxViews = Math.max(...timeStats.by_day_of_week.map(d => d.views));
                  const percentage = (day.views / maxViews) * 100;

                  return (
                    <div key={day.visit_day_of_week} className="stats-bar-item">
                      <span className="stats-bar-label">{getDayName(day.visit_day_of_week)}</span>
                      <div className="stats-bar-track">
                        <div
                          className="stats-bar-fill"
                          style={{ width: `${percentage}%` }}
                        >
                          <span className="stats-bar-value">{day.views?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="stats-empty">Aucune donnée disponible</div>
            )}
          </div>
        </div>

        {/* Trafic par heure */}
        <div className="stats-card">
          <div className="stats-card-header">
            <h2 className="stats-card-title">🕐 Trafic par heure</h2>
            <p className="stats-card-subtitle">Distribution journalière</p>
          </div>
          <div className="stats-chart-container stats-chart-scrollable">
            {timeStats?.by_hour && timeStats.by_hour.length > 0 ? (
              <div className="stats-bar-chart">
                {timeStats.by_hour.map((hour) => {
                  const maxViews = Math.max(...timeStats.by_hour.map(h => h.views));
                  const percentage = (hour.views / maxViews) * 100;

                  return (
                    <div key={hour.visit_hour} className="stats-bar-item">
                      <span className="stats-bar-label">{`${hour.visit_hour}h`}</span>
                      <div className="stats-bar-track">
                        <div
                          className="stats-bar-fill"
                          style={{ width: `${percentage}%` }}
                        >
                          <span className="stats-bar-value">{hour.views?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="stats-empty">Aucune donnée disponible</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
